import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Lock, Heart, Star } from "lucide-react";
import { toast } from "sonner";
import { useMarketplaceUnlock } from "../hooks/useMarketplaceUnlock";

// Premium faux-dashboard preview rendered from the card's accent color.
const DashboardMock = ({ accent }) => (
  <div className="relative w-[80%] rounded-xl bg-white shadow-[0_12px_32px_rgba(0,0,0,0.14)] overflow-hidden rotate-[-1.5deg] group-hover:rotate-0 group-hover:-translate-y-1.5 transition-all duration-500">
    <div className="flex items-center gap-1 px-2.5 py-1.5 border-b border-gray-100">
      <span className="w-1.5 h-1.5 rounded-full bg-red-300" />
      <span className="w-1.5 h-1.5 rounded-full bg-yellow-300" />
      <span className="w-1.5 h-1.5 rounded-full bg-green-300" />
    </div>
    <div className="flex">
      <div className="w-1/4 p-2 space-y-1.5 border-r border-gray-50">
        <div className="h-1.5 rounded-full" style={{ background: `${accent}55` }} />
        {[0, 1, 2].map((i) => <div key={i} className="h-1.5 rounded-full bg-gray-100" />)}
      </div>
      <div className="flex-1 p-2.5">
        <div className="flex gap-1.5 mb-2">
          <div className="h-6 flex-1 rounded-md" style={{ background: `${accent}1f` }} />
          <div className="h-6 flex-1 rounded-md bg-gray-100" />
          <div className="h-6 flex-1 rounded-md bg-gray-100" />
        </div>
        <svg viewBox="0 0 100 34" className="w-full h-11" preserveAspectRatio="none">
          <polygon points="0,30 14,20 30,25 46,11 62,16 78,5 100,9 100,34 0,34" fill={`${accent}1f`} />
          <polyline fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points="0,30 14,20 30,25 46,11 62,16 78,5 100,9" />
        </svg>
        <div className="flex gap-1.5 mt-2">
          <div className="h-1.5 flex-1 rounded-full bg-gray-100" />
          <div className="h-1.5 flex-1 rounded-full bg-gray-100" />
        </div>
      </div>
    </div>
  </div>
);

export const StartupCard = ({ startup, index = 0, gated = true }) => {
  const navigate = useNavigate();
  const mp = useMarketplaceUnlock();
  const g = startup.gradient || ["#D4AF37", "#0F0F0F"];
  const accent = g[0];
  const [saved, setSaved] = useState(false);

  const locked = gated && mp && !mp.unlocked;
  const act = (fn) => locked ? mp.guard() : fn();
  const featured = startup.featured ?? index === 0;
  const price = startup.price || 250;

  const toggleSave = (e) => {
    e.stopPropagation();
    setSaved((v) => !v);
    toast[saved ? "message" : "success"](saved ? "Removed from saved" : "Saved to your list");
  };

  return (
    <div
      onClick={() => { if (locked) mp.guard(); }}
      className={`group bg-white rounded-2xl border border-gray-100 shadow-[0_4px_20px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_50px_rgb(0,0,0,0.12)] hover:-translate-y-1 transition-all duration-300 flex flex-col overflow-hidden animate-fade-up ${locked ? "cursor-pointer" : ""}`}
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
      data-testid={`startup-card-${startup.id}`}
    >
      {/* Premium preview header */}
      <div
        className="relative h-44 flex items-center justify-center overflow-hidden"
        style={{ background: `linear-gradient(140deg, ${accent}2e 0%, ${accent}12 55%, #ffffff 100%)` }}
      >
        {featured && (
          <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 text-[10px] font-bold tracking-wide bg-gradient-to-r from-[#D4AF37] to-[#b8901f] text-[#0F0F0F] px-2.5 py-1 rounded-full shadow-sm" data-testid={`startup-featured-${startup.id}`}>
            <Star size={10} className="fill-[#0F0F0F]" /> Featured
          </span>
        )}
        <button
          onClick={toggleSave}
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm shadow flex items-center justify-center hover:scale-110 transition-transform"
          data-testid={`startup-save-${startup.id}`}
          aria-label="Save startup"
        >
          <Heart size={15} className={saved ? "fill-[#E1306C] text-[#E1306C]" : "text-[#687280]"} />
        </button>
        <DashboardMock accent={accent} />
      </div>

      <div className="p-5 flex flex-col flex-1">
        <span
          className="text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full w-fit"
          style={{ backgroundColor: `${accent}1f`, color: accent }}
          data-testid={`startup-category-${startup.id}`}
        >
          {startup.category}
        </span>
        <h3 className="font-display text-lg font-bold text-[#0F0F0F] mt-2.5">{startup.name}</h3>
        <p className="text-sm text-[#687280] mt-1 leading-snug line-clamp-2 flex-1">{startup.tagline}</p>

        <button
          onClick={() => act(() => navigate(`/apps/${startup.id}`))}
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 bg-[#0F0F0F] text-white rounded-full py-3 text-sm font-bold hover:bg-[#1c1c1c] transition-colors"
          data-testid={`startup-view-btn-${startup.id}`}
        >
          {locked ? <Lock size={14} /> : null} View App <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
};
